// Review-step filter/sort shared across the assessment flows. Callers pass `getId`
// mapping a list item to its question-id string, since both the item shape
// ({ q, num } pairs or bare questions) and the id field name differ per flow.

export const REVIEW_ANSWER_FILTERS = ['Yes', 'Partially', 'No', 'Not Applicable'];

// `filter`: 'all' | 'flagged' | 'commented' | one of REVIEW_ANSWER_FILTERS.
// `sort`: 'none' | 'flagged-first' | 'answer'. Sorts are stable, so the incoming
// (question-number) order is preserved within equal keys.
// `search`: free-text, matched case-insensitively against everything visible on a review
// row — the question text (`getText(item)`), its saved answer, and its note — not just the
// question wording. Same substring-match approach as the All Risks search (RiskRegister.js).
// `getText` is optional; omitting it just narrows the match to answer/note. An empty
// `search` skips the step entirely, so existing callers are unaffected.
export function filterAndSortReviewItems(items, {
    getId,
    getText,
    responses = {},
    notes = {},
    flaggedQuestions = new Set(),
    filter = 'all',
    sort = 'none',
    search = '',
} = {}) {
    let list = items || [];

    if (filter === 'flagged') {
        list = list.filter(it => flaggedQuestions.has(getId(it)));
    } else if (filter === 'commented') {
        list = list.filter(it => !!notes[getId(it)]);
    } else if (REVIEW_ANSWER_FILTERS.includes(filter)) {
        list = list.filter(it => responses[getId(it)] === filter);
    }

    const trimmedSearch = search.trim().toLowerCase();
    if (trimmedSearch) {
        list = list.filter(it => {
            const id = getId(it);
            const text = typeof getText === 'function' ? (getText(it) || '') : '';
            const answer = responses[id] || '';
            const note = notes[id] || '';
            return `${text} ${answer} ${note}`.toLowerCase().includes(trimmedSearch);
        });
    }

    if (sort === 'flagged-first') {
        list = [...list].sort((a, b) =>
            (flaggedQuestions.has(getId(a)) ? 0 : 1) - (flaggedQuestions.has(getId(b)) ? 0 : 1));
    } else if (sort === 'answer') {
        list = [...list].sort((a, b) =>
            (responses[getId(a)] || '').localeCompare(responses[getId(b)] || ''));
    }

    return list;
}
